import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { HelpOutline, Settings, Sync } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { TEMPLATE_STATUS, prettyKey, wa } from './waTheme';

/** Body with each {{n}} shown as a labelled chip-like token. */
const TemplateBody: React.FC<{ body?: string; variables: string[]; labels: Record<string, string> }> = ({ body, variables, labels }) => {
  if (!body) return <Typography variant="body2" color="text.secondary">Not linked to a Twilio template yet.</Typography>;
  const parts = body.split(/(\{\{\s*\d+\s*\}\})/g);
  return (
    <Typography component="div" sx={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap', color: '#111' }}>
      {parts.map((p, i) => {
        const m = p.match(/\{\{\s*(\d+)\s*\}\}/);
        if (!m) return <React.Fragment key={i}>{p}</React.Fragment>;
        const name = variables[Number(m[1]) - 1];
        return (
          <Box
            key={i}
            component="span"
            sx={{ px: 0.7, py: 0.1, mx: 0.2, borderRadius: 1, bgcolor: 'rgba(18,140,126,0.14)', color: wa.dark, fontWeight: 700, fontSize: 12 }}
          >
            {name ? labels[name] ?? name : `{{${m[1]}}}`}
          </Box>
        );
      })}
    </Typography>
  );
};

const TemplateGallery: React.FC<{
  templates: any;
  labels: Record<string, string>;
  studioId: string;
  canWrite: boolean;
  onManage: () => void;
  onRefreshed: () => void;
  notify: (msg: string, sev?: 'success' | 'error') => void;
}> = ({ templates, labels, studioId, canWrite, onManage, onRefreshed, notify }) => {
  const [syncing, setSyncing] = useState(false);
  const list: any[] = templates?.templates ?? [];
  const linked = list.filter((t) => t.providerTemplateId);
  const unlinked = list.filter((t) => !t.providerTemplateId);

  const sync = async () => {
    setSyncing(true);
    try {
      const res: any = await apiService.syncMessagingTemplates(studioId);
      notify(`Refreshed ${(res?.data ?? res)?.synced ?? 0} template(s) from WhatsApp`);
      onRefreshed();
    } catch (e: any) {
      notify(e?.message ?? 'Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} mb={2}>
        <Box flexGrow={1}>
          <Typography fontWeight={800}>
            {templates?.summary?.approved ?? 0} of {linked.length} linked templates approved
          </Typography>
          <Typography variant="body2" color="text.secondary">
            WhatsApp only delivers approved templates. Highlighted words are filled in for each member.
          </Typography>
        </Box>
        {canWrite && (
          <>
            <Button size="small" variant="outlined" startIcon={syncing ? <CircularProgress size={14} /> : <Sync />} onClick={sync} disabled={syncing} sx={{ borderColor: wa.teal, color: wa.teal, fontWeight: 700 }}>
              Refresh approval status
            </Button>
            <Button size="small" variant="contained" startIcon={<Settings />} onClick={onManage} sx={{ background: wa.cta, fontWeight: 700 }}>
              Link & submit templates
            </Button>
          </>
        )}
      </Stack>

      {!linked.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No templates linked yet. Create them in Twilio’s Content Template Builder, then use <b>Link & submit templates</b>.
        </Alert>
      )}

      <Grid container spacing={2}>
        {linked.map((t) => {
          const st = TEMPLATE_STATUS[t.status] ?? TEMPLATE_STATUS.draft;
          return (
            <Grid key={t.templateKey} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Box sx={{ height: '100%', borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 2, pt: 1.6, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography fontWeight={800} fontSize={14} flexGrow={1} noWrap>{prettyKey(t.templateKey)}</Typography>
                  <Chip size="small" label={st.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: st.color, bgcolor: st.bg }} />
                  {t.rejectionReason && (
                    <Tooltip title={t.rejectionReason}><HelpOutline sx={{ fontSize: 16, color: '#b71c1c' }} /></Tooltip>
                  )}
                </Box>
                <Box sx={{ mx: 2, p: 1.5, borderRadius: '0 12px 12px 12px', bgcolor: wa.chatBg, flexGrow: 1 }}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: '0 10px 10px 10px', p: 1.2, boxShadow: '0 1px 1px rgba(0,0,0,0.1)' }}>
                    <TemplateBody body={t.bodyPreview} variables={t.variableOrder ?? []} labels={labels} />
                  </Box>
                </Box>
                <Box sx={{ px: 2, py: 1.2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {(t.usedBy ?? []).length ? `Used for: ${(t.usedBy ?? []).map((u: any) => u.description).join(', ')}` : 'Available for workflows'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {!!unlinked.length && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight={700} color="text.secondary" mb={1}>
            Not linked yet ({unlinked.length})
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.8}>
            {unlinked.map((t) => (
              <Chip key={t.templateKey} size="small" variant="outlined" label={prettyKey(t.templateKey)} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default TemplateGallery;
